<template>
  <div class="Game">

    <!-- Masthead -->
    <ProjectMasthead
      title="Game Design"
      subtitle="Building A New Games Division."
      srcImage="/assets/other/game/weirdwood-preview.png"
      largeImage="/assets/other/game/weirdwood-preview@2x.png"
      bgColour="#E6A325" />


    <!-- Testimonial -->
    <Testimonial
      shortQuote="Weirdwood Manor is an absolute home run, and I simply cannot wait to jump back in."
      attributed="ComicBook.com"
    >
      <template slot="fullQuote">

        <p>"There's no shortage of compelling concepts on Kickstarter these days, especially in the board game space. That makes it even more impressive when a game is able to stand out from the multitude of projects vying for attention, and Greyridge Games' Weirdwood Manor is one of those true shining stars. Novel mechanics, layered character powers, stellar production, and an immersive theme are woven together to form a rich and satisfying experience that should not be missed."</p>
      </template>
    </Testimonial>


    <!-- Project Details -->
    <div class="details">


      <!-- Intro -->
      <ContentSection title="Introduction">
        <p>Weirdwood Manor is an original IP developed by Relish Studios, and it began its life as a hit iOS app (named one of Apple's Apps of The Year). In 2020, I was brought on board to bring Weirdwood Manor to the tabletop industry as a brand-new game, under a new game publishing division I was to build, called Greyridge Games.</p>

        <p>My mandate was both broad and deep:</p>

        <ul>
          <li>I was responsible for the design, development, and testing of the game</li>
          <li>Parallel to that I developed our go-to-market strategy for this new division</li>
          <li>I developed and oversaw the marketing strategy for the game</li>
          <li>I developed our manufacturing, shipping and logistics pipeline</li>
          <li>I handled the business development for the new division, including distribution and licensing deals</li>
          <li>I oversaw resourcing for the project and new division</li>
        </ul>

      </ContentSection>

      <!-- Market -->
      <!--
      <ContentSection title="Market Positioning &amp; Strategy">
        <p>This project kicked off with my investigations into the potential market for the game, and how to best position ourselves in the market as a new publisher. As someone with over 12 years in the board game hobby, and more than 6 years previous experience in designing games, I knew the market intimately, and I knew that we'd need an innovative design that would turn heads; somrthing to get us noticed in the vast sea for board games.
          
          
          We decided to focus on a strategy of honesty, and leaning into our small "first-time publisher" status. This led to a casual and frank style of communication with our growing audience. That was typified by some early YouTube videos we produced, showing people how we were going about bringing our first game to market.</p>

        <iframe width="560" height="315" src="https://www.youtube.com/embed/yfNnQwn9l4s" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
      </ContentSection>
      -->

      <!-- Design -->
      <ContentSection title="Design &amp; Development">
        <p>This project kicked off with my investigations into the potential market for the game, and how to best position ourselves in the market as a new publisher. As someone with over 12 years in the board game hobby, and more than 6 years previous experience in designing games, I knew the market intimately, and I knew that we'd need an innovative design that would turn heads; something to get us noticed in the vast sea of board games.</p>

        <p>To kick off the initial design, I ran some ideation sessions with our key stakeholders who had developed the Weirdwood universe and then, based on those outcomes, I prepared several game ideas and pitched those to that group and other stakeholders.</p>

        <ProjectImage
          srcImage="/assets/other/game/pitches-900.jpg"
          largeImage="/assets/other/game/pitches-1800.jpg"
          :canZoom="true"
          caption="Original game pitches" />

        <p>With further feedback, I then fleshed out a small subset of game design documents that we took to broader groups of board game designers and enthusiasts for feedback. After analyzing that feedback, I settled on a design idea that was a hybrid that incorporated the most compelling aspects from a couple of the design documents, and I got to work designing the game.</p>

        <p>My design featured an innovative modular game board with rotating rings that worked as both a timer in the game and as a means to alter the connections between rooms during gameplay. This temporal aspect to the game, with the board state shifting and changing during gameplay, became the core engine driving the game and made for a very compelling and unique game experience.</p>
        
        <p>Over the next several months, I ran through many iterations and prototypes based on this core mechanic, slowly ironing out the kinks and filling in the design gaps as we play-tested with groups of local play-testers. This video from a series we shot about making the game detailed the start of the earliest prototype of the board.</p>

        <div class="video"><iframe src="https://www.youtube.com/embed/1h4_XgdHW4c" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe></div>

        <p>Once I felt the game was playing generally well and our ruleset was formalized, I began broader playtesting, with a view to refining the balance and tweaking rules to better streamline the gameplay. We eventually play-tested the game with over 100 different people and had an average score of 8 out of 10 from all the tests we had run.</p>

        <p>I also began to integrate high fidelity components, graphic design and artwork in the game over this time. I did all of the UX design for the game, worked in conjunction with another graphic designer on the final graphic design, and hired an illustrator who I art-directed for our illustrations.</p>

        <ProjectImage
          srcImage="/assets/other/game/wwm-player-boards-900.jpg"
          largeImage="/assets/other/game/wwm-player-boards-1800.jpg"
          :canZoom="true"
          caption="A sample of the player board designs" />

        <ProjectImage
          srcImage="/assets/other/game/wwm-monster-boards-900.jpg"
          largeImage="/assets/other/game/wwm-monster-boards-1800.jpg"
          :canZoom="true"
          caption="A sample of the monster board designs" />

        <ProjectImage
          srcImage="/assets/other/game/wwm-cards-900.jpg"
          largeImage="/assets/other/game/wwm-cards-1800.jpg"
          :canZoom="true"
          caption="A sample of the action and companion cards" />

        <p>As the final play-testing came together with our final components, graphic design and illustration, I felt strongly that we had a great game on our hands and I oversaw the production of a promotional video for the game, which would become part of our marketing push towards our Kickstarter campaign.</p>

        <div class="video"><iframe src="https://www.youtube.com/embed/wD5qT9wyIkw" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe></div>

      </ContentSection>

      <!-- Kickstarter -->
      <ContentSection title="Marketing Strategy &amp; Kickstarter">
        <p>The core of my initial strategy is that we would be taking the game to Kickstarter. As a first-time publisher, entering the retail market directly would be exceptionally difficult, but I knew we could deploy a marketing strategy to build a strong and loyal audience before we got to Kickstarter. That would set us up for a strong first-day launch, which would be a key component for keeping momentum throughout the Kickstarter.</p>

        <p>To build that pre-Kickstarter audience in the year leading up to the campaign, we would focus on:</p>
        <ul>
          <li>Showing the game off at prominent board game conventions like GenCon and Essen Spiel</li>
          <li>Running paid ad campaigns</li>
          <li>Building awareness via social media (primarily Instagram and YouTube)</li>
          <li>Building awareness via prominent game reviewers and influencers</li>
        </ul>

        <p>Our first metric was to build an email list of subscribers interested in the game, and we hit our goal of 5000 subscribers before we launched on Kickstarter.</p>

        <p>Secondly, we secured coverage for the game across more than 30 prominent board game reviewers, including prominent industry channels like the Dice Tower to important cross-over outlets like ComicBook.com and GeekDad.com.</p>

        <p>With those efforts paying dividends, I then designed the entire Kickstarter campaign and ran it as well, with a focus on producing a very clean and efficient campaign. As a first-time publisher, I leaned into that story, keeping our communication and messaging casual and personal, to make certain our audience felt like they knew us as people and were able to relate to&mdash;and feel a part of&mdash;the journey we were taking.</p>

        <ProjectImage
          srcImage="/assets/other/game/wwm-ks-page-900.jpg"
          largeImage="/assets/other/game/wwm-ks-page-1800.jpg"
          :canZoom="true"
          caption="Parts of the Kickstarter campaign page" />

        <p>That strategy, and the work we did building a passionate audience before the campaign, helped us gain strong, lasting momentum on the campaign. Kickstarter took note of our efforts in the initial launch, and we were their featured game for many days during the campaign, and were even the featured project across all of Kickstarter's 3000+ projects for part of the campaign.</p>

      </ContentSection>

      <!-- BusDev -->
      <ContentSection title="Business Development">
        <p>My strategic work was not just focused on the marketing and KS campaign, but also on the fact that we would need relationships in place post-Kickstarter that would allow us to continue to grow and leverage any momentum we gained on the campaign.</p>
          
        <p>To that end, leading into the Kickstarter, I also began networking and doing due diligence in establishing reliable distribution and localization relationships that we could parley into deals to help bring the game to market&mdash;and expand our sales in retail.</p>

        <p>I also secured our manufacturing, fulfillment and freighting partners during this time, putting into place our entire production and delivery chain, so that would be ready once we were finished with the Kickstarter campaign. This work also included negotiating better manufacturing costs, reviewing materials and final designs for our three-dimensional components.</p>

        <ProjectImage
          srcImage="/assets/other/game/wwm-3d-900.jpg"
          largeImage="/assets/other/game/wwm-3d-1600.jpg"
          :canZoom="true"
          caption="Some of the 3d component designs" />

      </ContentSection>

      <!-- Outcome -->
      <ContentSection title="Outcome">
        <p>The game funded on Kickstarter in May 2023 with more than 5,000 backers and $494,000 raised. On the heels of that, I negotiated distribution agreements for retail, and with the help of our licensing partner, oversaw localization and publishing arrangements across a dozen other countries. Looking forward, we have continued to get rave reviews for the game and our audience has continued to grow substantially as people await a 2024 release of the game.</p>

        <ProjectImage
          srcImage="/assets/other/game/wwm-conclusion-900.jpg"
          largeImage="/assets/other/game/wwm-conclusion-1800.jpg"
          :canZoom="false" />
      </ContentSection>

    </div>

    <Pagination
      prevLabel="Other Mediums"
      nextLabel="Painting"
      prevRoute="Mediums"
      nextRoute="Painting" />

    <Footer />
  </div>
</template>

<script>
import ProjectMasthead from '@/components/ProjectMasthead.vue'
import Footer from '@/components/Footer.vue'
import Pagination from '@/components/Pagination.vue'
import Testimonial from '@/components/Testimonial.vue'
import ContentSection from '@/components/ContentSection.vue'
import ProjectImage from '@/components/ProjectImage.vue'

export default {
  name: 'Game',
  components: {
    ProjectMasthead,
    Footer,
    Pagination,
    Testimonial,
    ContentSection,
    ProjectImage
  },
}
</script>

<style lang="scss" scoped>
  .Game {
    background: #fff;

    & .video {
      position: relative;
      overflow: hidden;
      width: 100%;
      padding-top: 56.25%;
      margin: 0 0 20px 0;

      & iframe {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 100%;
      }
    }
    & ul {
      list-style: none;
      padding: 0 0 0 10px;

      & li {
        padding: 5px 0 4px 20px;
        position: relative;

        &:before {
          content: '';
          display: block;
          width: 4px;
          height: 4px;
          border-radius: 4px;
          border: 1px solid #000;
          position: absolute;
          left: 0;
          top: 18px;
        }
        & strong {
          display: block;
        }
      }
    }
  }
</style>
